/* React */
import React, { useEffect, useState } from 'react';

/* Translation */
import { useTranslation } from 'react-i18next';

/* Components */
import { Button, Selecter } from 'lego-dist';
import RendererIf from 'components/RendererIf';
import ResultActionModal from 'components/shared/ResultActionModal/ResultActionModal';

/* Styled Components */
import { CenterTab } from './styles/CenterGeneral';
import {
  ConfigHeader,
  SaveBtn,
  ConfigBody,
  DeactivateOverlay,
  ConfigItem,
  Label,
  WrapperSelecter,
  Warning,
  StyledModalResultsImage,
} from './styles/CenterConfig';

/* Constants, Hooks, Services */
import { ACTIVITIES_PER_DAY, DAYS_PER_WEEK, DAY_OF_WEEK } from 'constants/onBoarding';
import useAssignActivity from 'hooks/useAssignActivity';
import useCenters from 'hooks/centers/UseCenters';

/* Images, Icons */
import updateErrorImage from 'images/error.svg';
import updateSuccessImage from 'images/checked.svg';

interface CenterConfigProps {
  orgToken?: string;
  configuration?: any;
  center?: any;
}

const CenterConfig: React.FC<CenterConfigProps> = ({ center }) => {
  const { t } = useTranslation();
  const { updateCenterConfig, getCenterConfig } = useCenters();

  /* selecter values */
  const translatedWeekdays = DAY_OF_WEEK.map((item) => ({ ...item, name: t(`${item.label}`), label: t(`${item.label}`) }));
  const buildDaysPerWekk = DAYS_PER_WEEK.map((item) => ({ ...item, name: item.label }));
  const buildAactivitiesPerDay = ACTIVITIES_PER_DAY.map((item) => ({ ...item, name: item.label }));

  /* result modal logic */
  const [showResultActionModal, setShowResultActionModal] = useState<any>(
    false,
  );
  const ConfigResultActionModal = {
    success: {
      title: t('common:SUCCESS'),
      image: updateSuccessImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
    error: {
      title: t('common:ERROR'),
      image: updateErrorImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
  };
  const [statusResultActionModal, setStatusResultActionModal] = useState<any>(
    ConfigResultActionModal.error,
  );

  /* check if individual center configuration is enabled */
  const {
    centerConfiguration: orgConfig,
  } = useAssignActivity({ autoFetchs: true });

  const configEnabled = orgConfig?.mandatory;

  /* handle form data */
  const [formData, setFormData] = useState<any>(orgConfig || {});

  const handleOnSave = () => {
    const { plan_days, dap_initial_day, activities_per_day, mandatory } = formData;
    updateCenterConfig({
      center_configuration: {
        plan_days,
        activities_per_day,
        dap_initial_day,
        mandatory,
      },
    }, Number(center?.external_id)).then(() => {
      setStatusResultActionModal(ConfigResultActionModal.success);
    }).catch(() => {
      setStatusResultActionModal(ConfigResultActionModal.error);
    }).finally(() => {
      setShowResultActionModal(true);
    });
  };

  useEffect(() => {
    if (orgConfig?.mandatory) {
      setFormData(orgConfig || {});
    } else {
      getCenterConfig(center?.external_id)
        .then((response: any) => {
          setFormData(response?.center_configuration || {});
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.external_id, orgConfig]);

  const updateFormData = (values: any) => setFormData({ ...formData, ...values });

  return (
    <CenterTab>
      <RendererIf condition={showResultActionModal}>
        <ResultActionModal
          styles={{ Image: StyledModalResultsImage }}
          {...statusResultActionModal}
        />
      </RendererIf>
      <ConfigHeader>{t('settings:CENTER_SECTION.ORG_CONFIG.DAILY_ACTIVITY')}</ConfigHeader>
      <ConfigBody>
        {configEnabled ? <DeactivateOverlay /> : null}
        <ConfigItem>
          <Label>{t('settings:CENTER_SECTION.ORG_CONFIG.DAY_OF_THE_WEEK')}</Label>
          <WrapperSelecter>
            <Selecter
              placeholder={t('SELECT_OPTION')}
              data={translatedWeekdays}
              name="dap_initial_day"
              value={formData?.dap_initial_day}
              onChange={(dapInitialDay: any) => {
                if (updateFormData) updateFormData({ dap_initial_day: dapInitialDay.value });
              }}
            />
          </WrapperSelecter>
        </ConfigItem>
        <ConfigItem>
          <Label>{t('settings:CENTER_SECTION.ORG_CONFIG.DAYS_A_WEEK')}</Label>
          <WrapperSelecter>
            <Selecter
              placeholder={t('SELECT_OPTION')}
              data={buildDaysPerWekk}
              name="plan_days"
              value={formData?.plan_days}
              onChange={(planDays: any) => {
                if (updateFormData) updateFormData({ plan_days: planDays.value });
              }}
            />
          </WrapperSelecter>
        </ConfigItem>
        <ConfigItem>
          <Label>{t('settings:CENTER_SECTION.ORG_CONFIG.DEVELOPMENT_ACTIVITIES')}</Label>
          <WrapperSelecter>
            <Selecter
              placeholder={t('SELECT_OPTION')}
              name="activities_per_day"
              data={buildAactivitiesPerDay}
              value={formData?.activities_per_day}
              onChange={(activitiesPerDay: any) => {
                if (updateFormData) updateFormData({ activities_per_day: activitiesPerDay.value });
              }}
            />
          </WrapperSelecter>
        </ConfigItem>
      </ConfigBody>
      <SaveBtn>
        <RendererIf condition={!configEnabled}>
          <Button rounded flat color="#75B753" onClick={handleOnSave}>
            {t('SAVE')}
          </Button>
        </RendererIf>
      </SaveBtn>
      {
        configEnabled && <Warning>{t('settings:CENTER_SECTION.ORG_CONFIG.DAILY_ACTIVITY_WARNING')}</Warning>
      }
    </CenterTab>
  );
};

export default CenterConfig;
